<template>
  <div class="animated fadeIn">
    <b-row>
      <!-- ================= Payroll groups ================= -->
      <b-col lg="4" class="mb-3">
        <div class="card shadow-sm border-0">
          <div class="card-header bg-facebook text-white d-flex align-items-center justify-content-between py-2">
            <h6 class="mb-0"><font-awesome-icon icon="users" /> Payroll Groups</h6>
            <b-button variant="light" size="sm" @click="openGroup(null)">
              <font-awesome-icon icon="plus" /> New Group
            </b-button>
          </div>
          <div class="card-body p-0 position-relative">
            <b-row align-v="center" class="loader" v-if="loading_groups">
              <b-col class="text-center"><b-spinner></b-spinner></b-col>
            </b-row>
            <b-list-group flush>
              <b-list-group-item
                v-for="g in groups"
                :key="g.id"
                button
                :active="selected && selected.id === g.id"
                @click="selectGroup(g)"
                class="d-flex align-items-center justify-content-between"
              >
                <div>
                  <strong>{{ g.name }}</strong>
                  <b-badge v-if="!g.is_active" variant="secondary" class="ml-1">Inactive</b-badge>
                  <div class="small" :class="selected && selected.id === g.id ? 'text-white-50' : 'text-muted'">
                    {{ g.shift_types.length }} shift(s) &middot; {{ employeeCount(g) }} employee(s)
                  </div>
                </div>
                <div class="text-nowrap">
                  <b-button :variant="selected && selected.id === g.id ? 'light' : 'outline-primary'" size="sm" class="py-0" @click.stop="openGroup(g)">
                    <font-awesome-icon icon="edit" />
                  </b-button>
                  <b-button :variant="selected && selected.id === g.id ? 'light' : 'outline-danger'" size="sm" class="py-0" @click.stop="removeGroup(g)" v-if="g.shift_types.length === 0">
                    <font-awesome-icon icon="minus-circle" />
                  </b-button>
                </div>
              </b-list-group-item>
              <b-list-group-item v-if="!loading_groups && groups.length === 0" class="text-muted small text-center">
                No payroll group yet. Create one, then add its shifts.
              </b-list-group-item>
            </b-list-group>
          </div>
        </div>

        <b-alert show variant="info" class="small mt-3">
          <strong>Payroll group</strong> = who is paid and approved together (its own cutoff). Attendance batches, payrolls and scheduled OT are per group.
          <br /><br />
          <strong>Shift</strong> = the timetable inside a group: Time In, Break Start, Break End, Time Out, and the <strong>required duty hours</strong> that divide the daily rate into the hourly rate. Every employee is assigned to one shift on their record.
        </b-alert>
      </b-col>

      <!-- ================= Shifts of the selected group ================= -->
      <b-col lg="8">
        <div class="card shadow-sm border-0" v-if="selected">
          <div class="card-header bg-dark text-white d-flex align-items-center justify-content-between py-2">
            <h6 class="mb-0">
              <font-awesome-icon icon="clock" /> Shifts &mdash; {{ selected.name }}
              <b-badge v-if="!selected.is_active" variant="secondary" class="ml-1">Inactive group</b-badge>
            </h6>
            <b-button variant="success" size="sm" @click="openShift(null)">
              <font-awesome-icon icon="plus" /> Add Shift
            </b-button>
          </div>
          <div class="card-body p-0 position-relative">
            <b-row align-v="center" class="loader" v-if="loading_shifts">
              <b-col class="text-center"><b-spinner></b-spinner></b-col>
            </b-row>
            <div class="table-responsive mb-0">
              <table class="table table-sm table-bordered mb-0 align-middle">
                <thead class="thead-light">
                  <tr>
                    <th>Shift</th>
                    <th class="text-center">Schedule</th>
                    <th class="text-center">Duty</th>
                    <th class="text-center">Grace</th>
                    <th class="text-center">Early Scan</th>
                    <th class="text-center">OT Rate</th>
                    <th class="text-center">Employees</th>
                    <th class="text-center">Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="s in shifts" :key="s.id" :class="{ 'text-muted': !s.is_active }">
                    <td>
                      <strong>{{ s.description }}</strong>
                      <b-badge v-if="!s.is_active" variant="secondary" class="ml-1">Inactive</b-badge>
                    </td>
                    <td class="text-center text-nowrap small">
                      <template v-if="s.active_version">
                        {{ hm(s.active_version.am_time_in) }} &ndash; {{ hm(s.active_version.am_time_out) }}
                        <span class="text-muted">| break |</span>
                        {{ hm(s.active_version.pm_time_in) }} &ndash; {{ hm(s.active_version.pm_time_out) }}
                      </template>
                      <span v-else class="text-muted">&mdash;</span>
                    </td>
                    <td class="text-center">
                      <b-badge variant="dark">{{ hours(s.active_version ? s.active_version.required_duty_hours : s.required_duty_hours) }}</b-badge>
                    </td>
                    <td class="text-center small text-nowrap" v-if="s.active_version">
                      AM {{ s.active_version.am_time_in_grace }}m<b-badge v-if="Number(s.active_version.am_in_strict) === 1" variant="danger" class="ml-1" title="Strict: AM IN closes when the grace ends">strict</b-badge><b-badge v-if="Number(s.active_version.am_extension_enabled) === 1" variant="info" class="ml-1" :title="amExtensionText(s.active_version)">AM ext &ge;{{ s.active_version.am_extension_min_minutes }}m / max {{ s.active_version.am_extension_max_minutes }}m</b-badge>
                      &middot;
                      PM {{ s.active_version.pm_time_in_grace }}m<b-badge v-if="Number(s.active_version.pm_in_strict) === 1" variant="danger" class="ml-1" title="Strict: PM IN closes when the grace ends">strict</b-badge>
                    </td>
                    <td v-else></td>
                    <td class="text-center small">{{ s.active_version ? s.active_version.early_scan_minutes + 'm' : '' }}</td>
                    <td class="text-center">
                      <template v-if="s.active_version">
                        <b-badge v-if="Number(s.active_version.ot_multiplier_enabled) === 1" variant="info">{{ 100 + Number(s.active_version.ot_multiplier_percent || 0) }}%</b-badge>
                        <b-badge v-else variant="secondary">Off</b-badge>
                      </template>
                    </td>
                    <td class="text-center">
                      <b-button v-if="Number(s.manpowers_count) > 0" variant="link" class="p-0 font-weight-bold" title="View the employees on this shift" @click="openEmployees(s)">
                        {{ s.manpowers_count }}
                      </b-button>
                      <span v-else class="text-muted">0</span>
                    </td>
                    <td class="text-center">
                      <!-- Shift state first (matches the Active switch in the form); the
                           schedule version state (pending / in effect) is secondary. -->
                      <template v-if="!s.is_active">
                        <b-badge variant="secondary" title="The shift is switched off: its employees cannot punch on the kiosk.">Inactive</b-badge>
                        <small v-if="s.pending_version" class="d-block text-muted">schedule change pending</small>
                      </template>
                      <b-badge v-else-if="s.pending_version" variant="warning" :title="'Change effective ' + s.pending_version.formatted_effective_date.other_formats.format_1">
                        Pending {{ s.pending_version.formatted_effective_date.other_formats.format_3 }}
                      </b-badge>
                      <b-badge v-else-if="s.active_version" variant="success">Active</b-badge>
                      <b-badge v-else variant="secondary">No schedule</b-badge>
                    </td>
                    <td class="text-center text-nowrap">
                      <b-button variant="outline-primary" size="sm" class="py-0" title="Edit" @click="openShift(s)"><font-awesome-icon icon="edit" /></b-button>
                      <b-button variant="outline-dark" size="sm" class="py-0" title="History" @click="openHistory(s)"><font-awesome-icon icon="list" /></b-button>
                      <b-button variant="outline-danger" size="sm" class="py-0" title="Delete" @click="removeShift(s)" v-if="s.manpowers_count == 0"><font-awesome-icon icon="minus-circle" /></b-button>
                    </td>
                  </tr>
                  <tr v-if="!loading_shifts && shifts.length === 0">
                    <td colspan="9" class="text-center text-muted small py-3">No shift yet in this group. Add one so employees can be assigned to it.</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <b-alert v-else show variant="light" class="border text-muted">
          Select a payroll group on the left to manage its shifts.
        </b-alert>
      </b-col>
    </b-row>

    <!-- ================= Group modal ================= -->
    <b-modal v-model="group_modal" centered size="sm" :hide-footer="true" :title="group_form.id ? 'Edit Payroll Group' : 'New Payroll Group'">
      <b-form @submit.prevent="saveGroup">
        <b-form-group label="Name">
          <b-form-input v-model="group_form.name" required maxlength="100" placeholder="e.g. Office, Warehouse"></b-form-input>
        </b-form-group>
        <b-form-group v-if="group_form.id">
          <b-form-checkbox v-model="group_form.is_active" switch>Active</b-form-checkbox>
          <small class="text-muted">An inactive group is hidden from attendance, payroll and the employee form. Its history stays.</small>
        </b-form-group>
        <b-button type="submit" variant="success" block :disabled="saving"><font-awesome-icon icon="save" /> Save</b-button>
      </b-form>
    </b-modal>

    <!-- ================= Shift modal ================= -->
    <b-modal v-model="shift_modal" centered size="lg" :hide-footer="true" :title="shift_form.id ? ('Edit Shift — ' + shift_form.description) : ('New Shift — ' + (selected ? selected.name : ''))">
      <b-alert v-if="editing_shift && editing_shift.pending_version" show variant="danger" class="small">
        <font-awesome-icon icon="info-circle" /> A schedule change for this shift is <strong>pending</strong> and takes effect on
        <strong>{{ editing_shift.pending_version.formatted_effective_date.other_formats.format_1 }}</strong>.
        The schedule cannot be changed again until then (name and active switch can).
      </b-alert>

      <b-form @submit.prevent="saveShift">
        <b-row>
          <b-col md="8">
            <b-form-group label="Shift name">
              <b-form-input v-model="shift_form.description" required maxlength="256" placeholder="e.g. Office 8-5, Early shift 6-2"></b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="Required duty hours" description="Divides the daily rate into the hourly rate.">
              <b-form-input type="number" step="0.25" min="0.5" max="24" v-model.number="shift_form.required_duty_hours" required></b-form-input>
            </b-form-group>
          </b-col>
        </b-row>

        <table class="table table-bordered table-sm mb-2">
          <thead class="thead-dark">
            <tr>
              <th style="width:34%">Punch</th>
              <th style="width:28%">Scheduled Time</th>
              <th style="width:18%">Grace (mins)</th>
              <th style="width:20%" title="When on, this IN can no longer be scanned once the grace period has ended.">Strict mode</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in rows" :key="row.time_key">
              <td class="align-middle">
                <font-awesome-icon :icon="row.icon" :class="row.icon_class" /> <strong>{{ row.label }}</strong>
                <div class="small text-muted">{{ row.hint }}</div>
              </td>
              <td><b-form-input type="time" v-model="shift_form[row.time_key]" required size="sm"></b-form-input></td>
              <td>
                <b-form-input v-if="row.grace_key" type="number" min="0" max="1440" v-model.number="shift_form[row.grace_key]" required size="sm"></b-form-input>
                <span v-else class="text-muted">&mdash;</span>
              </td>
              <td class="align-middle">
                <template v-if="row.strict_key">
                  <b-form-checkbox v-model="shift_form[row.strict_key]" switch size="sm" class="d-inline-block">
                    <small :class="shift_form[row.strict_key] ? 'text-danger font-weight-bold' : 'text-muted'">
                      {{ shift_form[row.strict_key] ? 'closes ' + cutoffText(row) : 'off' }}
                    </small>
                  </b-form-checkbox>
                </template>
                <span v-else class="text-muted">&mdash;</span>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Live duty check -->
        <b-alert show :variant="summary ? (summary.matches ? 'success' : 'danger') : 'light'" class="small py-2 mb-3">
          <template v-if="summary && !summary.ordered">
            <font-awesome-icon icon="info-circle" /> Times must be in order: Time In, then Break Start, then Break End, then Time Out.
          </template>
          <template v-else-if="summary">
            <font-awesome-icon :icon="summary.matches ? 'check-circle' : 'info-circle'" />
            Time In to Time Out covers <strong>{{ hours(summary.span) }}</strong>, break <strong>{{ hours(summary.brk) }}</strong>
            &rarr; duty <strong>{{ hours(summary.duty) }}</strong>
            <span v-if="summary.matches">&mdash; matches the required {{ hours(shift_form.required_duty_hours) }}.</span>
            <span v-else>&mdash; does <strong>not</strong> match the required {{ hours(shift_form.required_duty_hours) }}. Adjust the times or the required hours.</span>
          </template>
          <template v-else>Fill in the four times to check them against the required duty hours.</template>
        </b-alert>

        <div class="border rounded p-2 mb-2">
          <div class="d-flex align-items-center justify-content-between">
            <div>
              <strong><font-awesome-icon icon="sign-out-alt" /> AM extension</strong>
              <div class="small text-muted">When on, staying past Break Start counts toward the required duty hours (regular rate, never OT) once it reaches the minimum below, up to the cap.</div>
            </div>
            <b-form-checkbox v-model="shift_form.am_extension_enabled" switch size="lg"></b-form-checkbox>
          </div>
          <b-row v-if="shift_form.am_extension_enabled" class="mt-2 pt-2 border-top align-items-end">
            <b-col cols="6" md="4">
              <label class="small font-weight-bold mb-1">Minimum (minutes)</label>
              <b-form-input type="number" min="0" max="1440" v-model.number="shift_form.am_extension_min_minutes" required size="sm"></b-form-input>
            </b-col>
            <b-col cols="6" md="4">
              <label class="small font-weight-bold mb-1">Cap (minutes)</label>
              <b-form-input type="number" min="1" max="1440" v-model.number="shift_form.am_extension_max_minutes" required size="sm"></b-form-input>
            </b-col>
            <b-col md="4" class="small text-muted">
              {{ amExtensionText(shift_form) }}
            </b-col>
          </b-row>
        </div>

        <div class="border rounded p-2 mb-2 d-flex align-items-center justify-content-between">
          <div>
            <strong><font-awesome-icon icon="clock" /> Early scan allowance (minutes)</strong>
            <div class="small text-muted">Break End (PM IN) can't be scanned earlier than this many minutes before its scheduled time.</div>
          </div>
          <b-form-input type="number" min="0" max="1440" v-model.number="shift_form.early_scan_minutes" required size="sm" style="width:100px"></b-form-input>
        </div>

        <div class="border rounded p-2 mb-2">
          <div class="d-flex align-items-center justify-content-between">
            <div>
              <strong><font-awesome-icon icon="chart-line" /> Apply OT multiplier</strong>
              <div class="small text-muted">On: each OT hour = hourly rate plus the premium below. Off: OT paid at the plain hourly rate (holiday OT then has no premium either).</div>
            </div>
            <b-form-checkbox v-model="shift_form.ot_multiplier_enabled" switch size="lg"></b-form-checkbox>
          </div>
          <div v-if="shift_form.ot_multiplier_enabled" class="d-flex align-items-center justify-content-between mt-2 pt-2 border-top">
            <div class="small">
              <strong>OT premium (%)</strong> &mdash; OT hour = hourly rate &times; <strong>{{ 100 + Number(shift_form.ot_multiplier_percent || 0) }}%</strong> on ordinary days.
              <span class="text-muted">Holidays keep the statutory 30% OT premium.</span>
            </div>
            <b-input-group size="sm" append="%" style="width:130px" class="flex-shrink-0 ml-3">
              <b-form-input type="number" min="0" max="500" step="0.01" v-model.number="shift_form.ot_multiplier_percent" required></b-form-input>
            </b-input-group>
          </div>
        </div>

        <div class="border rounded p-2 mb-3 d-flex align-items-center justify-content-between" v-if="shift_form.id">
          <div>
            <strong>Active</strong>
            <div class="small text-muted">An inactive shift can't be punched on the kiosk and is hidden from the employee form. Move its employees first.</div>
          </div>
          <b-form-checkbox v-model="shift_form.is_active" switch size="lg"></b-form-checkbox>
        </div>
        <b-alert v-if="shift_form.id && !shift_form.is_active && editing_shift && editing_shift.manpowers_count > 0" show variant="danger" class="small py-2 mb-3">
          <font-awesome-icon icon="info-circle" />
          <strong>{{ editing_shift.manpowers_count }}</strong> employee(s) are assigned to this shift. While it is inactive
          <strong>none of them can clock in on the kiosk</strong>. Switch it back on, or move them to another shift first.
        </b-alert>

        <b-alert show variant="warning" class="small mb-3">
          <font-awesome-icon icon="info-circle" /> A schedule change takes effect the <strong>next day</strong> once anyone on this shift has clocked in today, so recorded logs keep the schedule they were stamped with. If no one on this shift has clocked in yet today, it takes effect <strong>immediately</strong>.
        </b-alert>

        <b-button type="submit" variant="success" block :disabled="saving || !(summary && summary.matches)">
          <font-awesome-icon icon="save" /> Save Shift
        </b-button>
      </b-form>
    </b-modal>

    <!-- ================= History modal ================= -->
    <!-- Employees on a shift, with the full profile of the selected one -->
    <b-modal
      v-model="employees_modal" centered size="xl" modal-class="xxl-modal" body-class="p-0" :hide-footer="true"
      :title="employees_shift ? ('Employees — ' + employees_shift.description + ' (' + employees.length + ')') : 'Employees'"
    >
      <b-row class="m-0">
        <b-col md="4" class="p-0 border-right">
          <div class="p-2 border-bottom">
            <b-input-group size="sm">
              <b-input-group-prepend>
                <b-input-group-text class="bg-dark text-white"><font-awesome-icon icon="search" /></b-input-group-text>
              </b-input-group-prepend>
              <b-form-input v-model="employee_search" placeholder="Search name or ID" autocomplete="off"></b-form-input>
            </b-input-group>
          </div>
          <div class="text-center py-4" v-if="loading_employees"><b-spinner small></b-spinner></div>
          <div v-else class="list-group list-group-flush employees-list">
            <a
              v-for="e in filteredEmployees" :key="e.id" href="#"
              class="list-group-item list-group-item-action py-2"
              :class="{ active: selected_employee && selected_employee.id === e.id }"
              @click.prevent="selected_employee = e"
            >
              <div class="d-flex justify-content-between align-items-center">
                <strong class="text-uppercase small">{{ e.full_name }}</strong>
                <b-badge variant="light" class="border text-dark">{{ e.employment_status }}</b-badge>
              </div>
              <small :class="selected_employee && selected_employee.id === e.id ? 'text-white-50' : 'text-muted'">
                ID {{ e.id_number }} &middot; {{ e.job_title || 'No job title' }}
              </small>
            </a>
            <div v-if="filteredEmployees.length === 0" class="text-center text-muted small py-4">No employees found.</div>
          </div>
        </b-col>
        <b-col md="8" class="p-3 employees-profile">
          <!-- Re-mounted per employee (key) so the profile reloads on selection. -->
          <manpower-form v-if="selected_employee" :key="selected_employee.id" :manpower_id="selected_employee.id" />
          <div v-else class="text-center text-muted py-5">
            <font-awesome-icon icon="user" class="h1 mb-2" />
            <div>Select an employee on the left to view the profile.</div>
          </div>
        </b-col>
      </b-row>
    </b-modal>

    <b-modal v-model="history_modal" centered size="xl" :hide-footer="true" :title="history_shift ? ('Schedule History — ' + history_shift.description) : 'Schedule History'">
      <b-table responsive small bordered striped :items="history.data" :fields="history_fields" :tbody-tr-class="rowClass" show-empty head-variant="dark">
        <template v-slot:cell(status)="data">
          <b-badge v-if="data.item.id === activeHistoryId" variant="success">Active</b-badge>
          <b-badge v-else-if="isPending(data.item)" variant="warning">Pending</b-badge>
          <b-badge v-else variant="light">Superseded</b-badge>
        </template>
        <template v-slot:cell(effective_date)="data">
          <strong>{{ data.item.formatted_effective_date.other_formats.format_1 }}</strong>
        </template>
        <template v-slot:cell(am)="data">
          {{ hm(data.item.am_time_in) }} - {{ hm(data.item.am_time_out) }}
          <b-badge variant="light">grace {{ data.item.am_time_in_grace }}m</b-badge>
          <b-badge v-if="Number(data.item.am_in_strict) === 1" variant="danger">strict</b-badge>
          <b-badge v-if="Number(data.item.am_extension_enabled) === 1" variant="info" :title="amExtensionText(data.item)">AM ext &ge;{{ data.item.am_extension_min_minutes }}m / max {{ data.item.am_extension_max_minutes }}m</b-badge>
        </template>
        <template v-slot:cell(pm)="data">
          {{ hm(data.item.pm_time_in) }} - {{ hm(data.item.pm_time_out) }}
          <b-badge variant="light">grace {{ data.item.pm_time_in_grace }}m</b-badge>
          <b-badge v-if="Number(data.item.pm_in_strict) === 1" variant="danger">strict</b-badge>
        </template>
        <template v-slot:cell(duty)="data">{{ hours(data.item.required_duty_hours) }}</template>
        <template v-slot:cell(early)="data">{{ data.item.early_scan_minutes }}m</template>
        <template v-slot:cell(ot)="data">
          <b-badge v-if="Number(data.item.ot_multiplier_enabled) === 1" variant="info">{{ 100 + Number(data.item.ot_multiplier_percent || 0) }}%</b-badge>
          <b-badge v-else variant="secondary">Off</b-badge>
        </template>
        <template v-slot:cell(changed_by)="data">
          {{ data.item.created_by_user ? data.item.created_by_user.name : '-' }}
        </template>
      </b-table>
      <nav v-if="history.total > 0">
        <b-pagination @input="getHistory" align="right" :total-rows="parseInt(history.total)" :per-page="parseInt(history.per_page)" v-model="history.current_page" class="mb-0"></b-pagination>
      </nav>
    </b-modal>
  </div>
</template>

<script>
  import SwalMixin from '@/views/mixins/Swal.js'
  // The full employee profile (same component as Employees > Update).
  import ManpowerForm from '@/views/manpowers/manpower_form/Index.vue'

  const EMPTY_SHIFT = {
    id: null,
    payroll_group_id: null,
    description: '',
    is_active: true,
    am_time_in: '08:00', am_time_in_grace: 15, am_in_strict: false,
    am_time_out: '12:00',
    am_extension_enabled: false, am_extension_min_minutes: 20, am_extension_max_minutes: 90,
    pm_time_in: '13:00', pm_time_in_grace: 15, pm_in_strict: false,
    pm_time_out: '17:00',
    early_scan_minutes: 45,
    ot_multiplier_enabled: true,
    ot_multiplier_percent: 25,
    required_duty_hours: 8,
  };

  export default {
    name: 'PayrollGroups',
    mixins: [SwalMixin],
    components: { ManpowerForm },
    data() {
      return {
        loading_groups: true,
        loading_shifts: false,
        saving: false,
        groups: [],
        selected: null,
        shifts: [],
        // Grace applies only to the two INs (Time In and Break End).
        rows: [
          { label: 'Time In', hint: 'AM IN on the kiosk', time_key: 'am_time_in', grace_key: 'am_time_in_grace', strict_key: 'am_in_strict', icon: 'sign-in-alt', icon_class: 'text-success' },
          { label: 'Break Start', hint: 'AM OUT on the kiosk', time_key: 'am_time_out', grace_key: null, strict_key: null, icon: 'sign-out-alt', icon_class: 'text-warning' },
          { label: 'Break End', hint: 'PM IN on the kiosk', time_key: 'pm_time_in', grace_key: 'pm_time_in_grace', strict_key: 'pm_in_strict', icon: 'sign-in-alt', icon_class: 'text-success' },
          { label: 'Time Out', hint: 'PM OUT on the kiosk', time_key: 'pm_time_out', grace_key: null, strict_key: null, icon: 'sign-out-alt', icon_class: 'text-warning' },
        ],
        group_modal: false,
        group_form: { id: null, name: '', is_active: true },
        // Employees modal
        employees_modal: false,
        employees_shift: null,
        employees: [],
        loading_employees: false,
        employee_search: '',
        selected_employee: null,
        shift_modal: false,
        editing_shift: null,
        shift_form: Object.assign({}, EMPTY_SHIFT),
        history_modal: false,
        history_shift: null,
        history: { data: [], current_page: 1, total: 0, per_page: 1 },
        history_fields: [
          { key: 'status', label: 'Status' },
          { key: 'effective_date', label: 'Effective Date' },
          { key: 'am', label: 'Time In – Break Start' },
          { key: 'pm', label: 'Break End – Time Out' },
          { key: 'duty', label: 'Duty' },
          { key: 'early', label: 'Early Scan' },
          { key: 'ot', label: 'OT Rate' },
          { key: 'changed_by', label: 'Changed By' },
        ],
      }
    },
    computed: {
      today() {
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        return d;
      },
      // Live check: the four times must add up to the required duty hours.
      // duty = (Break Start - Time In) + (Time Out - Break End)
      summary() {
        const f = this.shift_form;
        const a = this.toMin(f.am_time_in), b = this.toMin(f.am_time_out), c = this.toMin(f.pm_time_in), d = this.toMin(f.pm_time_out);
        if (a === null || b === null || c === null || d === null) return null;
        const ordered = a < b && b <= c && c < d;
        const span = (d - a) / 60;
        const brk = (c - b) / 60;
        const duty = ((b - a) + (d - c)) / 60;
        const matches = ordered && Math.abs(duty - Number(f.required_duty_hours || 0)) < 0.009;
        return { ordered, span, brk, duty, matches };
      },
      filteredEmployees() {
        const term = this.employee_search.trim().toLowerCase();
        if (term === '') return this.employees;
        return this.employees.filter(e =>
          (e.full_name || '').toLowerCase().indexOf(term) !== -1 || String(e.id_number || '').toLowerCase().indexOf(term) !== -1
        );
      },
      activeHistoryId() {
        const rows = this.history.data || [];
        for (const r of rows) {
          if (new Date(r.effective_date + 'T00:00:00') <= this.today) return r.id;
        }
        return null;
      }
    },
    created() {
      this.getGroups();
    },
    methods: {
      /* ---------------- helpers ---------------- */
      toMin(t) {
        if (!t) return null;
        const p = t.split(':');
        if (p.length < 2) return null;
        return parseInt(p[0], 10) * 60 + parseInt(p[1], 10);
      },
      hm(value) {
        if (!value) return '--';
        const parts = value.split(':');
        let h = parseInt(parts[0], 10);
        const m = parts.length > 1 ? parts[1] : '00';
        const ampm = h >= 12 ? 'pm' : 'am';
        h = h % 12;
        if (h === 0) h = 12;
        return h + ':' + m + ' ' + ampm;
      },
      hours(value) {
        const n = Number(value);
        if (isNaN(n)) return '--';
        return (Math.round(n * 100) / 100).toString().replace(/\.0+$/, '') + 'h';
      },
      trimSeconds(value) {
        return value ? value.substring(0, 5) : '';
      },
      employeeCount(g) {
        return (g.shift_types || []).reduce((t, s) => t + Number(s.manpowers_count || 0), 0);
      },
      isPending(row) {
        return new Date(row.effective_date + 'T00:00:00') > this.today;
      },
      rowClass(item) {
        if (!item) return '';
        if (item.id === this.activeHistoryId) return 'table-success font-weight-bold';
        if (this.isPending(item)) return 'table-warning';
        return '';
      },
      fail(error, fallback) {
        this.swal_transaction_error_data.text = (error && error.response && error.response.data)
          ? (typeof error.response.data === 'string' ? error.response.data : (error.response.data.message || fallback))
          : fallback;
        this.swalRequestError();
      },

      /* ---------------- groups ---------------- */
      getGroups(keepSelection = true) {
        this.loading_groups = true;
        this.axios.get('payroll-groups/all').then(response => {
          this.groups = response.data;
          this.loading_groups = false;
          if (keepSelection && this.selected) {
            const still = this.groups.find(g => g.id === this.selected.id);
            this.selected = still || null;
          }
          if (!this.selected && this.groups.length) this.selectGroup(this.groups[0]);
        }).catch(error => {
          this.loading_groups = false;
          console.log(error);
        });
      },
      selectGroup(g) {
        this.selected = g;
        this.getShifts();
      },
      openGroup(g) {
        this.group_form = g
          ? { id: g.id, name: g.name, is_active: Number(g.is_active) === 1 }
          : { id: null, name: '', is_active: true };
        this.group_modal = true;
      },
      saveGroup() {
        this.saving = true;
        this.axios.post('payroll-groups/save', this.group_form).then(response => {
          this.saving = false;
          this.group_modal = false;
          this.swalTransactionCompleted();
          const saved = response.data;
          this.selected = { id: saved.id, name: saved.name, is_active: saved.is_active, shift_types: [] };
          this.getGroups();
        }).catch(error => {
          this.saving = false;
          this.fail(error, 'Unable to save the payroll group.');
        });
      },
      removeGroup(g) {
        this.swalConfirmDelete((data) => {
          if (data.value) {
            this.axios.delete('payroll-groups/delete', { data: { id: g.id } }).then(() => {
              this.swalTransactionCompleted();
              if (this.selected && this.selected.id === g.id) this.selected = null;
              this.getGroups();
            }).catch(error => this.fail(error, 'Unable to delete the payroll group.'));
          }
        });
      },

      /* ---------------- shifts ---------------- */
      getShifts() {
        if (!this.selected) return;
        this.loading_shifts = true;
        this.axios.get('shift-types/all', { params: { payroll_group_id: this.selected.id } }).then(response => {
          this.shifts = response.data;
          this.loading_shifts = false;
        }).catch(error => {
          this.loading_shifts = false;
          console.log(error);
        });
      },
      // "AM OUT from 12:20 counts, up to 1:30" preview for the AM extension.
      amExtensionText(v) {
        if (!v || !v.am_time_out) return '';
        const toText = (hhmm, add) => {
          const [h, m] = String(hhmm).substring(0, 5).split(':').map(Number);
          const total = h * 60 + m + Number(add || 0);
          const hh = Math.floor(total / 60) % 24, mm = total % 60;
          return ((hh % 12) || 12) + ':' + String(mm).padStart(2, '0') + ' ' + (hh >= 12 ? 'pm' : 'am');
        };
        const maxH = Number(v.am_extension_max_minutes || 0) / 60;
        return 'AM OUT at ' + toText(v.am_time_out, v.am_extension_min_minutes) + ' or later counts from ' + toText(v.am_time_out, 0)
          + ', up to ' + (maxH % 1 === 0 ? maxH : maxH.toFixed(2)) + ' hr' + (maxH === 1 ? '' : 's') + '.';
      },
      // "closes 8:15 am" preview for a strict IN: scheduled time + grace.
      cutoffText(row) {
        const time = this.shift_form[row.time_key];
        if (!time) return '';
        const [h, m] = time.split(':').map(Number);
        const total = h * 60 + m + Number(this.shift_form[row.grace_key] || 0);
        const hh = Math.floor(total / 60) % 24, mm = total % 60;
        const ampm = hh >= 12 ? 'pm' : 'am';
        return ((hh % 12) || 12) + ':' + String(mm).padStart(2, '0') + ' ' + ampm;
      },
      openEmployees(s) {
        this.employees_shift = s;
        this.employees = [];
        this.selected_employee = null;
        this.employee_search = '';
        this.employees_modal = true;
        this.loading_employees = true;
        this.axios.get('shift-types/employees', { params: { id: s.id } }).then(response => {
          this.employees = response.data || [];
          this.loading_employees = false;
        }).catch(error => {
          this.loading_employees = false;
          this.fail(error, 'Unable to load the employees of this shift.');
        });
      },
      openShift(s) {
        this.editing_shift = s;
        if (s) {
          // The working row = the newest schedule (pending or active).
          this.shift_form = {
            id: s.id,
            payroll_group_id: s.payroll_group_id,
            description: s.description,
            is_active: Number(s.is_active) === 1,
            am_time_in: this.trimSeconds(s.am_time_in), am_time_in_grace: Number(s.am_time_in_grace || 0), am_in_strict: Number(s.am_in_strict) === 1,
            am_time_out: this.trimSeconds(s.am_time_out),
            am_extension_enabled: Number(s.am_extension_enabled) === 1,
            am_extension_min_minutes: s.am_extension_min_minutes === null || s.am_extension_min_minutes === undefined ? 20 : Number(s.am_extension_min_minutes),
            am_extension_max_minutes: s.am_extension_max_minutes === null || s.am_extension_max_minutes === undefined ? 90 : Number(s.am_extension_max_minutes),
            pm_time_in: this.trimSeconds(s.pm_time_in), pm_time_in_grace: Number(s.pm_time_in_grace || 0), pm_in_strict: Number(s.pm_in_strict) === 1,
            pm_time_out: this.trimSeconds(s.pm_time_out),
            early_scan_minutes: Number(s.early_scan_minutes || 0),
            ot_multiplier_enabled: s.ot_multiplier_enabled === null || s.ot_multiplier_enabled === undefined ? true : Number(s.ot_multiplier_enabled) === 1,
            ot_multiplier_percent: s.ot_multiplier_percent === null || s.ot_multiplier_percent === undefined ? 25 : Number(s.ot_multiplier_percent),
            required_duty_hours: Number(s.required_duty_hours || 8),
          };
        } else {
          this.shift_form = Object.assign({}, EMPTY_SHIFT, { payroll_group_id: this.selected.id });
        }
        this.shift_modal = true;
      },
      saveShift() {
        if (!this.summary || !this.summary.matches) return;
        this.saving = true;
        this.axios.post('shift-types/save', this.shift_form).then(response => {
          this.saving = false;
          this.shift_modal = false;
          this.swal_transaction_completed_data.text = (response.data && response.data.message) ? response.data.message : 'Shift saved.';
          this.swalTransactionCompleted();
          this.getShifts();
          this.getGroups();
        }).catch(error => {
          this.saving = false;
          this.fail(error, 'Unable to save the shift.');
        });
      },
      removeShift(s) {
        this.swalConfirmDelete((data) => {
          if (data.value) {
            this.axios.delete('shift-types/delete', { data: { id: s.id } }).then(() => {
              this.swalTransactionCompleted();
              this.getShifts();
              this.getGroups();
            }).catch(error => this.fail(error, 'Unable to delete the shift.'));
          }
        });
      },
      openHistory(s) {
        this.history_shift = s;
        this.history = { data: [], current_page: 1, total: 0, per_page: 1 };
        this.history_modal = true;
        this.getHistory();
      },
      getHistory() {
        if (!this.history_shift) return;
        this.axios.get('shift-types/history', { params: { id: this.history_shift.id, page: this.history.current_page, per_page: 10 } })
          .then(response => { this.history = response.data; })
          .catch(error => console.log(error));
      }
    }
  }
</script>

<style scoped>
  /* Employees modal: independent scrolling for the list and the profile. */
  .employees-list {
    max-height: 72vh;
    overflow-y: auto;
  }
  .employees-profile {
    max-height: 78vh;
    overflow-y: auto;
  }
</style>
